import { forwardRef, type DragEvent, type FocusEvent, type KeyboardEvent, type PointerEvent } from "react";
import { Check } from "lucide-react";
import { cn } from "@/utils/helpers";
import { MEDIA_TYPE_LABEL, displayAspectRatio, type CalendarPlanSlot } from "@/types/contentCalendar.type";
import { pillarHue, type CalendarPost } from "./calendarModel";
import { ChannelMarks, TurnLine } from "./CalendarParts";
import { hoverPointer, peekKey } from "./peekIntent";

/**
 * Hover or keyboard focus shows a card's peek; a click opens the post. Touch
 * has no hover, so a tap opens the post with no peek.
 */
export interface PeekIntent {
  /** A mouse or pen came onto the card: the peek follows after the intent delay. */
  enter: (anchor: HTMLElement) => void;
  leave: () => void;
  /** Keyboard focus (focus-visible) shows the peek at once. */
  focus: (anchor: HTMLElement) => void;
  /** Where focus went, so moving into the peek keeps it. */
  blur: (next: Element | null) => void;
  /** Tab on the card: move focus into this card's peek if it is showing. True when it did. */
  intoPeek: () => boolean;
}

/** The card's handlers for a PeekIntent. Touch pointers are left to the click. */
export function peekHandlers(peek: PeekIntent | undefined) {
  if (!peek) return {};
  return {
    onPointerEnter: (event: PointerEvent<HTMLElement>) => {
      if (hoverPointer(event.pointerType)) peek.enter(event.currentTarget);
    },
    onPointerLeave: (event: PointerEvent<HTMLElement>) => {
      if (hoverPointer(event.pointerType)) peek.leave();
    },
    onFocus: (event: FocusEvent<HTMLElement>) => {
      if (event.target === event.currentTarget && event.currentTarget.matches(":focus-visible")) {
        peek.focus(event.currentTarget);
      }
    },
    onBlur: (event: FocusEvent<HTMLElement>) => {
      if (event.target === event.currentTarget) peek.blur(event.relatedTarget as Element | null);
    },
    // Capture, so the card's own key handler (Enter, Space, the lift) is untouched.
    onKeyDownCapture: (event: KeyboardEvent<HTMLElement>) => {
      if (event.target !== event.currentTarget) return;
      if (peekKey({ key: event.key, shift: event.shiftKey, where: "card", peekOpenForCard: true }) !== "into-peek") return;
      if (peek.intoPeek()) event.preventDefault();
    },
  };
}

/** "Video 9:16", "Static 4:5 · 5 slides": the format as the card reads it. */
export function formatLabel(slot: CalendarPlanSlot): string {
  const slides = slot.mediaType === "STATIC" && (slot.itemCount ?? 0) >= 2 ? ` · ${slot.itemCount} slides` : "";
  return `${MEDIA_TYPE_LABEL[slot.mediaType]} ${displayAspectRatio(slot)}${slides}`;
}

/**
 * CalendarPostCard (DESIGN.md "The Calendar in Parts"): a post in a month day.
 * The surface, radius 12 and no hairline, a 3px left edge in the post's pillar colour (no
 * pillar, no edge), the title, the format and channel marks, then whose turn it
 * is. A picture only once a file is uploaded; nothing in its place before.
 */
const CalendarPostCard = forwardRef<
  HTMLDivElement,
  {
    post: CalendarPost;
    /** Filtered out: faded to 30%, not hidden. */
    dimmed?: boolean;
    /** Lifted for a move: what stays behind is a faint outline. */
    lifted?: boolean;
    /** The card being dropped (Calendar / Drag preview's accent edge). A peeked card stays as it is. */
    active?: boolean;
    /** Shown only when the file is uploaded and its picture is known. */
    coverUrl?: string | null;
    draggable?: boolean;
    /** The card's accessible description of what it can do. */
    hint?: string;
    /** How many title lines this day's height can hold. Two at the 150px floor. */
    titleLines?: 2 | 3 | 4;
    onOpen: (anchor: HTMLElement) => void;
    /** Hover or keyboard focus shows the peek. */
    peek?: PeekIntent;
    onLift?: () => void;
    onDragStart?: (event: DragEvent<HTMLDivElement>) => void;
    onDragEnd?: () => void;
    onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  }
>(function CalendarPostCard(
  {
    post,
    dimmed = false,
    lifted = false,
    active = false,
    coverUrl = null,
    draggable = false,
    hint,
    titleLines = 2,
    onOpen,
    peek,
    onLift,
    onDragStart,
    onDragEnd,
    onKeyDown,
  },
  ref,
) {
  const { slot, state } = post;
  const hue = pillarHue(post.pillarIndex);
  const cover = slot.deliverable && coverUrl ? coverUrl : null;
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={dimmed ? -1 : 0}
      data-post-id={slot.id}
      aria-label={`${slot.title}. ${formatLabel(slot)}. ${state.turn.label}.${post.posted ? " Posted." : ""}${hint ? ` ${hint}` : ""}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={(event) => onOpen(event.currentTarget)}
      {...peekHandlers(peek)}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;
        if (event.key === "Enter") {
          event.preventDefault();
          onOpen(event.currentTarget);
        } else if (event.key === " " && onLift) {
          event.preventDefault();
          onLift();
        }
      }}
      className={cn(
        // Calendar / Post card (style B): radius 12 on the surface, no hairline;
        // the pillar is a 3px edge clipped by the corners.
        "group/card relative flex w-full min-w-0 cursor-pointer flex-col gap-[7px] overflow-hidden rounded-card bg-co-surface pb-2.5 pl-2.5 pr-2.5 pt-[9px] text-left outline-none",
        "transition-[box-shadow,opacity] duration-[var(--duration-short)] ease-[var(--ease-out-expo)]",
        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-co-accent",
        active && "shadow-[inset_0_0_0_2px_var(--co-accent)]",
        draggable && "active:cursor-grabbing",
        dimmed && "opacity-30",
        lifted && "opacity-40",
      )}
    >
      {hue ? (
        <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-[3px]" style={{ background: hue }} />
      ) : null}
      {cover ? (
        <span className="block max-h-24 overflow-hidden rounded-tag">
          <img src={cover} alt="" className="aspect-[4/5] w-full object-cover" loading="lazy" />
        </span>
      ) : null}
      <span
        className={cn(
          // Two lines at most, whatever the row height, so the status line always shows (Emad, 13 Oct).
          "min-h-0 text-[13px] font-semibold leading-[1.32] text-co-text line-clamp-2",
          titleLines === 4 || titleLines === 3 ? "line-clamp-2" : "line-clamp-2",
        )}
      >
        {slot.title}
      </span>
      {/* Figma 08: the channels sit right after the format, not pushed to the edge. */}
      <span className="flex min-w-0 shrink-0 items-center gap-1.5">
        <span className="truncate text-[12.5px] leading-[17px] text-co-muted">{formatLabel(slot)}</span>
        <ChannelMarks channels={post.channels} />
      </span>
      <span className="flex min-w-0 shrink-0 items-center gap-1.5">
        {/* Long turns wrap under their dot ("With Fatafeat · 7 days late"). */}
        <TurnLine owner={state.turn.owner} label={state.turn.label} alert={state.turn.alert} wrap />
        {post.posted ? (
          <Check aria-hidden="true" className="ml-auto size-3.5 shrink-0 text-co-live" strokeWidth={2.25} />
        ) : null}
      </span>
    </div>
  );
});

export default CalendarPostCard;
